import { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { adminApi, type TeamCallback } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import '../pages/live/live.css';
import { motionDisabled } from '@/shared/lib/motion';

interface AgentOption { id: number; name: string }

/**
 * Collapsible supervisor panel listing the moked's pending follow-ups with the
 * assigned agent + time. Per row: reassign to another agent, or take it yourself.
 */
export function TeamCallbacksPanel({ meId }: { meId?: number }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<TeamCallback[]>([]);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  // פופאובר "שייך לנציג" — מזהה הפולו-אפ שהרשימה שלו פתוחה.
  const [pickFor, setPickFor] = useState<number | null>(null);
  // מונה פתיחות — מחליף key כדי שהשורות ייכנסו מחדש בכל פתיחה (ולא בעדכון רשימה).
  const [openCount, setOpenCount] = useState(0);
  // אחרי שהפתיחה הסתיימה מבטלים את overflow:hidden כדי שרשימת "שייך לנציג" לא תיחתך.
  const [settled, setSettled] = useState(false);
  // התוכן נשאר ב-DOM עד סוף אנימציית הסגירה, ואז יורד (בלי כפתורים נסתרים ב-Tab).
  const [mounted, setMounted] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    void adminApi.teamCallbacks()
      .then(setItems)
      .catch((e) => {
        const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
        toast(msg ?? 'שגיאה בטעינת פולו-אפים', 'danger');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    void adminApi.agents({ per_page: 200 })
      .then((r) => setAgents(((r.data ?? []) as AgentOption[]).map((a) => ({ id: a.id, name: a.name }))))
      .catch(() => {});
  }, []);

  async function assign(id: number, userId: number) {
    setBusy(id);
    try {
      await adminApi.assignTeamCallback(id, userId);
      toast('הפולו-אפ שוייך מחדש', 'ok');
      load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בשיוך', 'danger');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card padded={false}>
      <button
        type="button"
        onClick={() => {
          // בלי תנועה (reduce / טסטים) אין transitionend — מסיימים מיד.
          const instant = motionDisabled();
          setSettled(instant);
          if (!open) { setOpenCount((n) => n + 1); setMounted(true); } else if (instant) setMounted(false);
          setOpen((o) => !o);
        }}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8, padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: 'inherit', color: 'var(--text)', fontSize: 15, fontWeight: 600,
        }}
      >
        <span>פולו-אפים של המוקד {items.length ? `(${items.length})` : ''}</span>
        <Icon name="chevronD" size={18} style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform .35s cubic-bezier(.2,.8,.2,1)' }} />
      </button>

      <div className="lm-collapse" data-open={open}
        onTransitionEnd={(e) => { if (e.target !== e.currentTarget) return; if (open) setSettled(true); else setMounted(false); }}>
      <div key={openCount} style={{ overflow: open && settled ? 'visible' : 'hidden' }}>
      {(open || mounted) && (
        loading ? (
          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--glass-line-2)', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>טוען…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--glass-line-2)', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין פולו-אפים ממתינים.</div>
        ) : (
          <div>
            {items.map((c, i) => {
              const due = dueLabel(c.scheduled_at);
              return (
                <div key={c.id} className="hover-row" style={{
                  display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '12px 20px', borderTop: '1px solid var(--glass-line-2)',
                  animation: `gcUp .4s var(--ease-out) ${i * 0.05}s backwards`,
                }}>
                  {/* שם + טלפון */}
                  <div style={{ flex: '1 1 180px', display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.35 }}>
                    <span style={{ fontWeight: 600 }}>{c.name || 'לא מזוהה'}</span>
                    <span dir="ltr" style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'end', fontVariantNumeric: 'tabular-nums' }}>{c.phone}</span>
                  </div>
                  {/* מועד — אדום כשבאיחור */}
                  <span title={new Date(c.scheduled_at).toLocaleString('he-IL')}
                    // באיחור ולא משויך — מהבהב (כמו dueAnim בעיצוב)
                    className={due.overdue && !c.agent_id ? 'anim-blink' : undefined}
                    style={{ flex: '0 0 120px', fontSize: 12.5, fontWeight: 600, color: due.overdue ? 'var(--danger)' : 'var(--text-2)' }}>
                    {due.text}
                  </span>
                  {/* סיבה */}
                  <span style={{ flex: '1 1 170px', fontSize: 12 }}>
                    {c.reason && (
                      <span style={{ padding: '2px 9px', borderRadius: 'var(--r-pill)', background: 'var(--accent-soft)', color: 'var(--accent-ink)' }}>{c.reason}</span>
                    )}
                  </span>
                  {/* נציג משויך */}
                  <span style={{ flex: '0 0 120px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-2)', minWidth: 0 }}>
                    <span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', flex: 'none', background: c.agent_name ? 'var(--accent)' : 'var(--faint)' }} />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.agent_name ?? 'לא משויך'}</span>
                  </span>
                  {/* פעולות */}
                  <div style={{ flex: '0 0 auto', display: 'flex', gap: 6, position: 'relative' }}>
                    {meId != null && c.agent_id !== meId && (
                      <button type="button" className="btn primary" style={{ height: 32, padding: '0 14px', fontSize: 12.5, borderRadius: 7 }}
                        disabled={busy === c.id} onClick={() => assign(c.id, meId)}>
                        אני לוקח
                      </button>
                    )}
                    <button type="button" className="btn" style={{ height: 32, padding: '0 14px', fontSize: 12.5, borderRadius: 7 }}
                      aria-label="שייך לנציג אחר" aria-expanded={pickFor === c.id}
                      disabled={busy === c.id} onClick={() => setPickFor((p) => (p === c.id ? null : c.id))}>
                      שייך לנציג
                    </button>
                    {pickFor === c.id && (
                      <>
                        <div onClick={() => setPickFor(null)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} aria-hidden />
                        <div role="menu" style={{
                          animation: 'gcUp .22s var(--ease-out) backwards', position: 'absolute', top: 'calc(100% + 6px)', insetInlineEnd: 0, zIndex: 31, width: 230, maxHeight: 280, overflowY: 'auto',
                          background: 'var(--glass-2)', border: '1px solid var(--glass-line)', boxShadow: 'var(--shadow-2)', borderRadius: 12, padding: 6,
                        }}>
                          {agents.filter((a) => a.id !== c.agent_id).length === 0 ? (
                            <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--muted)' }}>אין נציגים זמינים</div>
                          ) : agents.filter((a) => a.id !== c.agent_id).map((a) => (
                            <button key={a.id} type="button" role="menuitem" className="dash-row"
                              onClick={() => { setPickFor(null); void assign(c.id, a.id); }}
                              style={{ width: '100%', display: 'flex', alignItems: 'center', padding: '8px 10px', border: 0, borderRadius: 7, background: 'transparent', color: 'var(--text)', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', textAlign: 'start' }}>
                              {a.name}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
      </div>
      </div>
    </Card>
  );
}

/** "באיחור · 25 דק׳" / "עכשיו" / "היום 14:45" / תאריך קצר. */
function dueLabel(iso: string, now: number = Date.now()): { text: string; overdue: boolean } {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return { text: iso, overdue: false };
  const diff = Math.round((now - t) / 1000);
  if (diff > 60) {
    const ago = diff < 3600 ? `${Math.floor(diff / 60)} דק׳` : diff < 86400 ? `${Math.floor(diff / 3600)} שע׳` : `${Math.floor(diff / 86400)} ימים`;
    return { text: `באיחור · ${ago}`, overdue: true };
  }
  if (diff >= -60) return { text: 'עכשיו', overdue: false };
  const d = new Date(t);
  const time = d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === new Date(now).toDateString()) return { text: `היום ${time}`, overdue: false };
  return { text: `${d.toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric' })} ${time}`, overdue: false };
}
